import { ReactNode, useEffect } from 'react';
import clsx from 'clsx';

/**
 * MobileSheet — bottom sheet on mobile, side drawer (or centered modal) on desktop.
 *
 * On mobile (< md): slides up from bottom with rounded-t-2xl drag-handle pill.
 * On tablet+ (>= md): right-side drawer at fixed width, same as Sheet.
 *
 * Usage:
 *   <MobileSheet open={open} onClose={onClose} title="Log Payment">
 *     ...content...
 *   </MobileSheet>
 */
export function MobileSheet({
  open,
  onClose,
  title,
  children,
  maxWidthMd = 'w-[480px]',
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  maxWidthMd?: string;
}) {
  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <div
      className={clsx(
        'fixed inset-0 z-40 transition-opacity duration-[120ms]',
        open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
      )}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-ink/30" onClick={onClose} />

      {/* Mobile: bottom sheet */}
      <div
        className={clsx(
          'md:hidden absolute bottom-0 left-0 right-0 bg-bg rounded-t-2xl hairline-t transition-transform duration-200 ease-out',
          'max-h-[92vh] flex flex-col',
          open ? 'translate-y-0' : 'translate-y-full',
        )}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <span className="h-1 w-10 rounded-full bg-ink/20" />
        </div>
        {/* Title row */}
        {title && (
          <div className="hairline-b px-4 pb-3 shrink-0 flex items-center justify-between">
            <span className="text-sm font-medium">{title}</span>
            <button
              onClick={onClose}
              className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-whisper text-muted hover:text-ink text-base"
              aria-label="Close"
            >✕</button>
          </div>
        )}
        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1">{children}</div>
      </div>

      {/* Desktop: right-side drawer */}
      <div
        className={clsx(
          'hidden md:flex absolute right-0 top-0 bottom-0 bg-bg hairline-l flex-col transition-transform duration-200 ease-out',
          maxWidthMd,
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {title && (
          <div className="hairline-b px-4 h-14 flex items-center justify-between shrink-0">
            <span className="text-sm font-medium">{title}</span>
            <button
              onClick={onClose}
              className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-whisker text-muted hover:text-ink"
              aria-label="Close"
            >✕</button>
          </div>
        )}
        <div className="p-4 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}

